import { m } from 'framer-motion';
import { useState, useCallback, useEffect, useRef } from 'react';
// @mui
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import List from '@mui/material/List';
import Stack from '@mui/material/Stack';
import Badge from '@mui/material/Badge';
import Drawer from '@mui/material/Drawer';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
// hooks
import { useBoolean } from 'src/hooks/use-boolean';
import { useResponsive } from 'src/hooks/use-responsive';
// components
import Label from 'src/components/label';
import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import { varHover } from 'src/components/animate';
//
import NotificationItem, { OnboardingItem } from './notification-item';
import { Invitation, useInvitationsQuery, WorkspaceList } from 'src/context/api/workspaces';
import { Avatar, Button, LinearProgress, ListItemAvatar, ListItemButton, ListItemText, Skeleton, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import LoadingButton from '@mui/lab/LoadingButton';
import { useGetBrandsQuery } from 'src/context/api/competitorsMonitoring/api';
import { setFilterAdvertizerPLA, setFilterBrandPLA, setFilterCountry, setFilterDomainPLA, setFilterKeyword, setNotificationBrandInfringement, setNotificationBrandInfringementTXT, setFilterDomainTXT, setFilterBrandTXT, setNotificationBrandInNonBrandCampaignsFilterEnabled } from 'src/context/reducers/filter-settings/filterSlice';
import { setAdType } from 'src/context/reducers/visibility/adsSlice';
import { useCurrentWorkspace } from 'src/context/reducers/app-settings';
import { useRouter } from 'src/routes/hooks';
import { Job, useExportJobsQuery, useExportMutation } from 'src/context/api/data/api';
import useNotifications from './use-notifications';
import { useCurrentUserQuery } from 'src/context/api/auth/api';

// ----------------------------------------------------------------------


// ----------------------------------------------------------------------

/**
 * Notifications Popover
 * 
 * This component is used to display the notifications popover.
 * It is used to display the notifications, export jobs, and other information.
 * 
 * @returns {JSX.Element} The Notifications Popover component
 */
export default function NotificationsPopover() {
  const drawer = useBoolean();
  const { t } = useTranslation();
  const smUp = useResponsive('up', 'sm');
  const filterStore = useSelector((state: any) => state.filters);
  const [currentTab, setCurrentTab] = useState('all');
  const dispatch = useDispatch();
  const workspace: any = useCurrentWorkspace();
  const theme: any = useTheme();
  const [exportSelected, setExportSelected] = useState<"new" | "previous">("new");
  const user = useCurrentUserQuery();


  const handleChangeTab = useCallback((event: React.SyntheticEvent, newValue: string) => {
    setCurrentTab(newValue);
  }, []);

  const [retryExport] = useExportMutation();

  const invitations = useInvitationsQuery(null);

  const { data: exportJobs, isLoading: isLoadingExportJobs, isFetching: isFetchingExportJobs, refetch: refetchExportJobs } = useExportJobsQuery(undefined, { pollingInterval: 1 * 90 * 1000 });

  const { notifications, isLoadingNotifications, isFetchingNotifications } = useNotifications();

  const renderHead = (
    <Stack direction="row" alignItems="center" sx={{ py: 2, pl: 2.5, pr: 1, minHeight: 68 }}>
      <Typography variant="h6" sx={{ flexGrow: 1 }}>
        Notifications
      </Typography>

      {!smUp && (
        <IconButton onClick={drawer.onFalse}>
          <Iconify icon="mingcute:close-line" />
        </IconButton>
      )}
    </Stack>
  );

  const role = user.data?.role?.toLowerCase();

  const unreadNotificationsCount =
    role === "customer" || role === "consultant"
      ? 0
      : (notifications?.filter((n) => n.status === "unread").length ?? 0);

  const TABS = [
    {
      value: 'all',
      label: t('dashboard.global.all'),
      count:
        (invitations.data?.length ?? 0) +
        (filterStore?.notificationBrandInNonBrandCampaigns ? 1 : 0) +
        (workspace?.showPlaAlert &&
          (filterStore?.notificationBrandInfringementNotification?.ads?.length ?? 0) > 0
          ? 1
          : 0) +
        (workspace?.showTxtAlert &&
          (filterStore?.notificationBrandInfringementNotificationTXT?.ads?.length ?? 0) > 0
          ? 1
          : 0) +
        unreadNotificationsCount,
    },
    {
      value: 'export',
      label: t('dashboard.global.export'),
      count: exportJobs?.jobs?.filter((job) => job.status === "completed").length ?? 0,
    },
    {
      value: 'onboard',
      label: 'Onboarding',
      count: notifications?.filter((notification) => notification.status === "unread" && notification.title === "Google user accounts retrieved").length ?? 0,
    },
  ];

  if (user.data?.role.toLowerCase() === "customer" || user.data?.role.toLowerCase() === "consultant") {
    TABS.splice(2, 1);
  }

  const renderTabs = (
    <Tabs value={currentTab} onChange={handleChangeTab}>
      {TABS.map((tab) => (
        <Tab
          key={tab.value}
          iconPosition="end"
          value={tab.value}
          label={tab.label}
          icon={
            <Label
              variant={((tab.value === 'all' || tab.value === currentTab) && 'filled') || 'soft'}
              color={
                (tab.value === 'unread' && 'info') ||
                (tab.value === 'archived' && 'success') ||
                'default'
              }
            >
              {tab.count}
            </Label>
          }
          sx={{
            '&:not(:last-of-type)': {
              mr: 3,
            },
          }}
        />
      ))}
    </Tabs>
  );

  const { data: brands } = useGetBrandsQuery({ bqTable: workspace?.bqTable }, { skip: !workspace?.bqTable });

  const renderAvatar = (
    <ListItemAvatar>
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{
          width: 40,
          height: 40,
          borderRadius: '50%',
          bgcolor: 'background.neutral',
        }}
      >
        <Box sx={{
          p: 1,
          backgroundColor: theme.palette.mode === 'dark' ? '#28323D' : ' #F4F6F8',
          color: theme.palette.mode === 'dark' ? '#ffffff' : '#000000',
          borderRadius: '10px',
          padding: '10px',
          width: 44
        }}>
          <Box component="img" src={`https://www.google.com/s2/favicons?domain=${workspace?.url}&sz=64`} sx={{ width: 24, height: 24, borderRadius: '5px' }} />
        </Box>
      </Stack>
    </ListItemAvatar>
  );

  const renderList = (
    <Scrollbar>
      <List disablePadding>
        {invitations?.data?.map((invitation: Invitation) => (
          <NotificationItem key={invitation.id} notification={invitation} />
        )) || [1, 3, 4, 5].map((index) => <Skeleton sx={{ margin: 2 }} variant="rounded" key={index} width={"95%"} height={75} />)}
      </List>
    </Scrollbar>
  );

  const handleDrawerNotification = () => {
    drawer.onTrue();
  }

  const renderOnboardList = user.data?.role.toLowerCase() !== "customer" && (
    <>
      <Stack direction="row" justifyContent="space-evenly" alignItems="center" spacing={2} sx={{ mt: 1 }}>
        <Typography sx={{ cursor: 'pointer', borderBottom: exportSelected === "new" ? `2px solid ${theme.palette.primary.main}` : 'none', fontWeight: exportSelected === "new" ? 600 : 400 }} variant="body2" color={exportSelected === "new" ? "primary" : "text.secondary"} onClick={() => setExportSelected("new")}>unread</Typography>
        <Typography sx={{ cursor: 'pointer', borderBottom: exportSelected === "previous" ? `2px solid ${theme.palette.primary.main}` : 'none', fontWeight: exportSelected === "previous" ? 600 : 400 }} variant="body2" color={exportSelected === "previous" ? "primary" : "text.secondary"} onClick={() => setExportSelected("previous")}>read</Typography>
      </Stack>
      <Scrollbar>
        <List disablePadding>
          {notifications?.filter((notification) => (exportSelected === "new" ? notification.status === "unread" : notification.status !== "unread") && notification.title === "Google user accounts retrieved").map((notification) => (
            <OnboardingItem read={notification.read} dismiss={notification.dismiss} key={notification.id} title={notification.title} content={notification.content} workspaceId={notification.workspace_id ?? 0} workspace={notification.workspace as WorkspaceList} />
          )) || [1, 3, 4, 5].map((index) => <Skeleton sx={{ margin: 2 }} variant="rounded" key={index} width={"95%"} height={75} />)}
        </List>
      </Scrollbar>
    </>
  );

  const router = useRouter();

  return (
    <>
      <IconButton
        component={m.button}
        whileTap="tap"
        whileHover="hover"
        variants={varHover(1.05)}
        color={drawer.value ? 'primary' : 'default'}
        onClick={handleDrawerNotification}
      >
        <Badge
          badgeContent={
            (invitations.data?.length ?? 0) +
            ((filterStore?.notificationBrandInNonBrandCampaigns) ? 1 : 0) +
            (workspace?.showPlaAlert && (filterStore?.notificationBrandInfringementNotification?.ads?.length ?? 0) > 0 ? 1 : 0) +
            (workspace?.showTxtAlert && (filterStore?.notificationBrandInfringementNotificationTXT?.ads?.length ?? 0) > 0 ? 1 : 0)}
          color="error"
        >
          <Iconify icon="solar:bell-bing-bold-duotone" width={24} />
        </Badge>
      </IconButton>

      <Drawer
        open={drawer.value}
        onClose={drawer.onFalse}
        anchor="right"
        slotProps={{
          backdrop: { invisible: true },
        }}
        PaperProps={{
          sx: { width: 1, maxWidth: 420 },
        }}
      >
        {renderHead}

        <Divider />

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ pl: 2.5, pr: 1 }}
        >
          {renderTabs}
        </Stack>

        <Divider />

        {workspace?.showPlaAlert && currentTab !== 'export' && currentTab !== 'onboard' &&
          <>
            {filterStore?.notificationBrandInfringementNotification?.ads &&
              filterStore.notificationBrandInfringementNotification.ads.length !== 0 && (
                <>
                  <ListItemButton
                    disableRipple
                    sx={{
                      p: 2.5,
                      alignItems: 'flex-start',
                      backgroundColor: 'rgba(255, 86, 48, 0.08)',
                      borderBottom: (theme: any) => `dashed 1px ${theme.palette.divider}`,
                    }}
                  >
                    {renderAvatar}

                    <Box
                      sx={{
                        top: 26,
                        width: 8,
                        height: 8,
                        right: 20,
                        borderRadius: '50%',
                        bgcolor: '#ec6240',
                        position: 'absolute',
                      }}
                    />

                    <Stack sx={{ flexGrow: 1, color: theme.palette.mode === "dark" ? '#e1a381' : "#B71D18", fontWeight: 800 }}>
                      Brand Infringement (PLA)
                      <ListItemText
                        disableTypography
                        secondary={
                          <Stack
                            direction="row"
                            alignItems="center"
                            sx={{ typography: 'caption', color: theme.palette.mode === "dark" ? '#e1a381' : "#B71D18" }}
                            divider={
                              <Box
                                sx={{
                                  width: 2,
                                  height: 2,
                                  bgcolor: 'currentColor',
                                  mx: 0.5,
                                  borderRadius: '50%',
                                }}
                              />
                            }
                          >
                            Some competitors are using your Brand in their Product Listing Ads Content.
                          </Stack>
                        }
                      />
                      <Stack spacing={1} direction="row" sx={{ mt: 1.5 }}>
                        <LoadingButton onClick={() => {
                          dispatch(setFilterBrandPLA(['__BRAND_INFRINGEMENT__'] as any));
                          dispatch(setAdType('PLA'));
                          dispatch(setFilterKeyword([]));
                          dispatch(setFilterCountry([]));
                          dispatch(setFilterDomainPLA([]));
                          dispatch(setFilterAdvertizerPLA([]));
                          dispatch(setNotificationBrandInfringement(false));
                          router.push(`/visibility/ads?workspace=${workspace?.id}`);
                          drawer.onFalse()
                        }} size="small" variant="contained" sx={{ backgroundColor: '#ec6240', color: 'white' }}>
                          Show
                        </LoadingButton>
                      </Stack>

                    </Stack>
                  </ListItemButton>
                </>
              )}
          </>
        }

        {workspace?.showTxtAlert && currentTab !== 'export' && currentTab !== 'onboard' &&
          <>
            {filterStore?.notificationBrandInfringementNotificationTXT?.ads &&
              filterStore.notificationBrandInfringementNotificationTXT.ads.length !== 0 && (
                <>
                  <ListItemButton
                    disableRipple
                    sx={{
                      p: 2.5,
                      alignItems: 'flex-start',
                      backgroundColor: 'rgba(255, 86, 48, 0.08)',
                      borderBottom: (theme: any) => `dashed 1px ${theme.palette.divider}`,
                    }}
                  >
                    {renderAvatar}

                    <Box
                      sx={{
                        top: 26,
                        width: 8,
                        height: 8,
                        right: 20,
                        borderRadius: '50%',
                        bgcolor: '#ec6240',
                        position: 'absolute',
                      }}
                    />

                    <Stack sx={{ flexGrow: 1, color: '#e1a381', fontWeight: 800 }}>
                      Brand Infringement (Text Ads)
                      <ListItemText
                        disableTypography
                        secondary={
                          <Stack
                            direction="row"
                            alignItems="center"
                            sx={{ typography: 'caption', color: '#e1a381' }}
                            divider={
                              <Box
                                sx={{
                                  width: 2,
                                  height: 2,
                                  bgcolor: 'currentColor',
                                  mx: 0.5,
                                  borderRadius: '50%',
                                }}
                              />
                            }
                          >
                            Some competitors are using your Brand in their Text Ads Content.
                          </Stack>
                        }
                      />
                      <Stack spacing={1} direction="row" sx={{ mt: 1.5 }}>
                        <LoadingButton onClick={() => {
                          dispatch(setFilterBrandPLA(['__BRAND_INFRINGEMENT__'] as any));
                          dispatch(setAdType('TEXT'));
                          dispatch(setFilterKeyword([]));
                          dispatch(setFilterCountry([]));
                          dispatch(setFilterDomainPLA([]));
                          dispatch(setFilterDomainTXT([]));
                          dispatch(setNotificationBrandInfringementTXT(false));
                          router.push(`/visibility/ads?workspace=${workspace?.id}`);
                          drawer.onFalse()
                        }} size="small" variant="contained" sx={{ backgroundColor: '#ec6240', color: 'white' }}>
                          Show
                        </LoadingButton>
                      </Stack>

                    </Stack>
                  </ListItemButton>
                </>
              )}
          </>
        }


        {filterStore?.notificationBrandInNonBrandCampaigns && currentTab !== 'export' && currentTab !== 'onboard' &&
          <>
            <ListItemButton
              disableRipple
              sx={{
                p: 2.5,
                alignItems: 'flex-start',
                backgroundColor: 'rgba(255, 86, 48, 0.08)',
                borderBottom: (theme: any) => `dashed 1px ${theme.palette.divider}`,
              }}
            >
              {renderAvatar}

              <Box
                sx={{
                  top: 26,
                  width: 8,
                  height: 8,
                  right: 20,
                  borderRadius: '50%',
                  bgcolor: '#ec6240',
                  position: 'absolute',
                }}
              />

              <Stack sx={{ flexGrow: 1, color: '#e1a381', fontWeight: 800 }}>
                Brand Campaigns Isolation
                <ListItemText
                  disableTypography
                  secondary={
                    <Stack
                      direction="row"
                      alignItems="center"
                      sx={{ typography: 'caption', color: '#e1a381' }}
                      divider={
                        <Box
                          sx={{
                            width: 2,
                            height: 2,
                            bgcolor: 'currentColor',
                            mx: 0.5,
                            borderRadius: '50%',
                          }}
                        />
                      }
                    >
                      Non-brand campaigns are targeting branded search terms.
                    </Stack>
                  }
                />
                <Stack spacing={1} direction="row" sx={{ mt: 1.5 }}>
                  <LoadingButton onClick={() => {
                    dispatch(setNotificationBrandInNonBrandCampaignsFilterEnabled(true)); router.push(`/optimization/campaigns?workspace=${workspace?.id}`);
                  }} size="small" variant="contained" sx={{ backgroundColor: '#ec6240', color: 'white' }}>
                    Show
                  </LoadingButton>
                </Stack>

              </Stack>
            </ListItemButton>
          </>
        }
        {currentTab === 'export' && (exportJobs?.jobs?.length ?? 0) > 0 && (
          <Scrollbar>
            <List disablePadding>
              {exportJobs?.jobs?.filter((exportItem: any) => {


                if (exportSelected === "new") {
                  return exportItem.status !== "downloaded";
                } else {
                  return exportItem.status === "downloaded";
                }
              }).map((exportItem: any, index: number) => (
                <ListItemButton
                  key={index}
                  disableRipple
                  sx={{
                    p: 2.5,
                    alignItems: 'flex-start',
                    backgroundColor: ['completed', 'downloaded'].includes(exportItem.status)
                      ? 'rgba(34, 197, 94, 0.08)'
                      : exportItem.status === 'failed'
                        ? 'rgba(255, 86, 48, 0.08)'
                        : 'rgba(255, 171, 0, 0.08)',
                    borderBottom: (theme: any) => `dashed 1px ${theme.palette.divider}`,
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Avatar>
                      <Iconify icon="iwwa:file-csv" width={24} />
                    </Avatar>
                  </Stack>
                  <Box
                    sx={{
                      top: 26,
                      width: 8,
                      height: 8,
                      right: 20,
                      borderRadius: '50%',
                      bgcolor: ['completed', 'downloaded'].includes(exportItem.status)
                        ? '#22c55e'
                        : exportItem.status === 'failed'
                          ? '#ec6240'
                          : '#ffab00',
                      position: 'absolute',
                    }}
                  />

                  <Stack sx={{
                    flexGrow: 1,
                    color: ['completed', 'downloaded'].includes(exportItem.status)
                      ? theme.palette.mode === "dark" ? '#4ade80' : '#16a34a'
                      : exportItem.status === 'failed'
                        ? theme.palette.mode === "dark" ? '#e1a381' : "#B71D18"
                        : theme.palette.mode === "dark" ? '#fbbf24' : '#d97706',
                    fontWeight: 800
                  }} pl={2}>
                    {exportItem.name}

                    <ListItemText
                      disableTypography
                      secondary={
                        <Stack
                          direction="row"
                          alignItems="center"
                          sx={{
                            typography: 'caption',
                            color: ['completed', 'downloaded'].includes(exportItem.status)
                              ? theme.palette.mode === "dark" ? '#4ade80' : '#16a34a'
                              : exportItem.status === 'failed'
                                ? theme.palette.mode === "dark" ? '#e1a381' : "#B71D18"
                                : theme.palette.mode === "dark" ? '#fbbf24' : '#d97706'
                          }}
                          divider={
                            <Box
                              sx={{
                                width: 2,
                                height: 2,
                                bgcolor: 'currentColor',
                                mx: 0.5,
                                borderRadius: '50%',
                              }}
                            />
                          }
                        >
                          {['completed', 'downloaded'].includes(exportItem.status) && 'Export completed successfully. Ready for download.'}
                          {exportItem.status === 'failed' && 'Export failed. Please try again.'}
                          {exportItem.status === 'running' && 'Export in progress. Please wait...'}
                          {exportItem.status === 'pending' && 'Export queued for processing.'}
                          {exportItem.status === 'created' && 'Export created. Please wait...'}
                        </Stack>
                      }
                    />

                    <Stack spacing={1} direction="row" sx={{ mt: 1.5 }}>
                      {['completed', 'downloaded'].includes(exportItem.status) && (
                        <LoadingButton
                          onClick={() => {
                            window.open(process.env.CROSS_API_URL + `/traffic/export/${exportItem.id}/download`, '_blank');
                            refetchExportJobs();
                          }}
                          size="small"
                          variant="contained"
                          sx={{ backgroundColor: '#22c55e', color: 'white' }}
                        >
                          Download
                        </LoadingButton>
                      )}

                      {exportItem.status === 'failed' && (
                        <Button size="small" variant="contained" color="error" onClick={() => {
                          refetchExportJobs();
                        }}>
                          dismiss
                        </Button>
                      )}

                      {(exportItem.status === 'running' || exportItem.status === 'pending') && (
                        <LinearProgress sx={{ width: 100 }} value={50} />
                      )}
                    </Stack>
                  </Stack>
                </ListItemButton>
              ))}
            </List>
          </Scrollbar>
        )}

        {currentTab !== 'export' && currentTab !== 'onboard' && renderList}
        {currentTab === 'onboard' && renderOnboardList}
      </Drawer>
    </>
  );
}
